<script>
    import {poiStore} from '@js/stores/poiStore';

    import I18n from '@js/modules/translations';
    import {activateOnKey} from '@js/modules/a11y';

    import Icon from '@js/components/themes/Icon.svelte';
    import Switch from '@js/components/themes/Switch.svelte';
    import Dropdown from '@js/components/themes/Dropdown.svelte';
    import textEditIcon from '@js/components/icons/textEditIcon';
    import trashIcon from '@js/components/icons/trashIcon';

    import {PLAN_SORTS, groupPlanPois, planPoiPinHtml, planPoiPlace, planPoiPeriodLabel, planPoiState} from '@js/modules/plan/planPois';

    const {
        sort = 'date',
        reminders = null,
        onRemindersChange,
        onSortChange,
        onShowOnMap,
        onEdit,
        onDelete
    } = $props();

    const sections = $derived(groupPlanPois($poiStore.planPois, sort));

    const filtersApplied = $derived(Object.values($poiStore.selectedFilters || {}).some(Boolean));

    const _handleSortChange = (sortKey, event) => {
        event.preventDefault();

        onSortChange(sortKey);
    };

    const rideSummary = (ride) => [ride.formattedDistance, ride.formattedDuration, ride.levelTranslated].filter(Boolean).join(' · ');
</script>

{#if $poiStore.planPois.length || filtersApplied}
    <section class="mt-8"
             aria-labelledby="plan-list-title">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 id="plan-list-title"
                class="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                {I18n.t('js.ride.plan.index.list.title')}
                <span class="text-base font-medium text-gray-500">
                    ({$poiStore.planPois.length})
                </span>
            </h2>

            <Dropdown color="gray"
                      buttonClass="rounded-full! font-medium"
                      selected={sort}
                      optionSize={PLAN_SORTS.length}
                      closeClickInside={true}>
                {#snippet dropdownButtonRender()}
                    {I18n.t('js.ride.plan.index.list.sort.current', {sort: I18n.t(`js.ride.plan.index.list.sort.${sort}`)})}
                {/snippet}

                <ul class="text-sm text-gray-700 dark:text-gray-200"
                    role="listbox"
                    aria-label={I18n.t('js.ride.plan.index.list.sort.label')}>
                    {#each PLAN_SORTS as sortKey (sortKey)}
                        <li class="p-2 cursor-pointer hover:bg-gray-300"
                            class:bg-primary-200={sort === sortKey}
                            role="option"
                            aria-selected={sort === sortKey}
                            tabindex="0"
                            onclick={_handleSortChange.bind(null, sortKey)}
                            onkeydown={activateOnKey(_handleSortChange.bind(null, sortKey))}>
                            {I18n.t(`js.ride.plan.index.list.sort.${sortKey}`)}
                        </li>
                    {/each}
                </ul>
            </Dropdown>
        </div>

        {#if reminders !== null}
            <div class="mb-4">
                <Switch checked={reminders}
                        labels={{true: I18n.t('js.ride.plan.index.reminders.label')}}
                        onchange={(event) => onRemindersChange(event.target.checked)}/>
            </div>
        {/if}

        {#if !$poiStore.planPois.length}
            <p class="py-10 text-center text-gray-600">
                {I18n.t('js.ride.plan.index.list.empty')}
            </p>
        {/if}

        {#each sections as section (section.key)}
            <div class="mb-6">
                {#if section.label}
                    <h3 class="mb-1 text-sm font-semibold uppercase tracking-wide"
                        class:text-yellow-800={section.key === 'overdue'}
                        class:text-gray-600={section.key !== 'overdue'}>
                        {section.label}
                        <span class="font-normal">({section.planPois.length})</span>
                    </h3>
                {/if}

                <ul class="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:divide-gray-700">
                    {#each section.planPois as planPoi (planPoi.id)}
                        <li class="px-3 py-2.5">
                            <div class="flex items-center gap-2">
                                <button type="button"
                                        class="flex flex-1 min-w-0 items-center gap-3 text-left rounded-lg cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-300"
                                        onclick={() => onShowOnMap(planPoi)}>
                                    <!-- eslint-disable-next-line svelte/no-at-html-tags -- built from constants (icon paths, state), no user text -->
                                    <span class="shrink-0">{@html planPoiPinHtml(planPoi)}</span>

                                    <span class="min-w-0 flex-1">
                                        <span class="block font-semibold text-gray-900 truncate dark:text-white">
                                            {planPoi.title}
                                        </span>

                                        <span class="block text-xs text-gray-600 truncate dark:text-gray-300">
                                            {[planPoiPlace(planPoi), planPoiPeriodLabel(planPoi)].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>

                                    {#if planPoiState(planPoi) === 'overdue' && section.key !== 'overdue'}
                                        <span class="shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-900">
                                            {I18n.t('js.ride.plan.index.list.overdue')}
                                        </span>
                                    {/if}

                                    <span class="sr-only">{I18n.t('js.ride.plan.index.list.show_on_map')}</span>
                                </button>

                                <button type="button"
                                        class="shrink-0 p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-300"
                                        title={I18n.t('js.ride.plan.index.list.edit')}
                                        onclick={() => onEdit(planPoi)}>
                                    <Icon class="w-5 h-5"
                                          icon={textEditIcon}
                                          label={I18n.t('js.ride.plan.index.list.edit')}/>
                                </button>

                                <button type="button"
                                        class="shrink-0 p-2 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-700 cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-300"
                                        title={I18n.t('js.ride.plan.index.list.delete')}
                                        onclick={() => onDelete(planPoi)}>
                                    <Icon class="w-5 h-5"
                                          icon={trashIcon}
                                          label={I18n.t('js.ride.plan.index.list.delete')}/>
                                </button>
                            </div>

                            {#if planPoi.associatedRides?.length}
                                <div class="mt-1.5 ms-12 flex flex-wrap gap-2">
                                    {#each planPoi.associatedRides as associatedRide (associatedRide.id)}
                                        <a class="inline-flex items-center max-w-full px-2.5 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
                                           href={associatedRide.link}>
                                            <span class="truncate underline">{associatedRide.name}</span>

                                            {#if rideSummary(associatedRide)}
                                                <span class="shrink-0 ms-1 text-gray-600 dark:text-gray-300">· {rideSummary(associatedRide)}</span>
                                            {/if}
                                        </a>
                                    {/each}
                                </div>
                            {/if}
                        </li>
                    {/each}
                </ul>
            </div>
        {/each}
    </section>
{/if}
